// client/src/services/lexistarNotifications.js
//
// LexiStar notifications API client (ST-00188, M3-A2; contracts
// docs/lexistar/contracts/m3-contracts.md §3.2, §7.2; LS-09 §5.16). Server:
// server/modules/lexistar/notifications, mounted at /api/lexistar.
//
// Thin wrapper over the shared axios instance in `services/api` (cookie-session auth,
// CSRF header, single-flight 401 refresh; `api` already carries `/api`). Every helper
// resolves to the response BODY. Payloads use the DB column names (snake_case). Action
// routes use the contract's `:verb` suffix literally (POST /notification-rules/:id:test).
//
// Response shapes:
//   listRules / getRule / createRule / updateRule   { rules[] } / { rule }
//     rule = { id, tenant_id, name, active, event_filter {types[], categories[], severities[],
//              conditions [{path, op, value}]}, recipients [{kind, ref?, channel?}], channels[],
//              template_key, throttle {per_rule_s, dedupe_s, quiet_hours {from, to, tz} | null,
//              digest {every_s} | null, severity_floor 'M' | {sms: 'H'} | null},
//              escalation [{after_s, recipients[], channels[], template_key?}], version, … }
//   testRule           { rule_id, event_id, matches_filter, enqueued: [{message_id, channel,
//                        state, in_app_id}], previews: [{channel, language, body, buttons,
//                        segments?, template {key, tenant_override, version, provider_status}}] }
//   listGroups / createGroup / updateGroup   { groups[] } / { group }
//   listTemplates      { tenant_id, templates: [row + source 'platform'|'tenant' + effective] }
//   updateTemplate     { template, overrides }
//   previewTemplate    { key, channel, language, language_fallback, body, buttons, characters,
//                        segments? (sms: {encoding, characters, segments}),
//                        whatsapp? {params[], provider_body}, template {source, version, …} }
//   getBudgets / updateBudgets   { tenant_id, month, channels: [{channel, budget, spend
//                        {messages, units, amount_zar}, soft_cap_reached, hard_cap_reached}] }
//   getTenantSettings / updateTenantSettings   { settings, seeded }
//   listMessages       { messages[], pagination }   getMessage { message, outbox[], cost[], chain[] }
//   getCostLedger      { month, entries[], totals[], total_zar, pagination }
//   getDeliverySlo     { from, to, windows[], totals[] }
//   listNotifications  { notifications: [{id, title, body, link, severity, read_at, created_at,
//                        message_id}], unread }
//   markNotificationsRead   { marked, ids[], unread }
//
// Realtime: the server joins every /tracking socket with a session to `user:{its user id}`
// at connect and emits `lexistar:notification` ({id, message_id, tenant_id, title, body,
// link, severity, created_at}) there. joinUserRoom() re-joins explicitly (ack {ok, room}).

import api from './api';

const LX = '/lexistar';

const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
const body = (res) => res.data;
/** Arrays become comma lists (channel, state accept either). */
const query = (params = {}) =>
  clean(
    Object.fromEntries(
      Object.entries(params).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v])
    )
  );

// ── Rules ────────────────────────────────────────────────────────────────────

/** Filters: active, q. */
export const listRules = (params) =>
  api.get(`${LX}/notification-rules`, { params: query(params) }).then(body);

export const getRule = (id) => api.get(`${LX}/notification-rules/${id}`).then(body);

/**
 * { name, event_filter, recipients: [{kind: 'user'|'role'|'group'|'driver'|'dispatcher'|
 *   'contact'|'telegram_group', ref?, channel?}], channels: ordered ['in_app','telegram',…],
 *   template_key?, throttle?, escalation?, active? } — `webhook` is rejected this round.
 */
export const createRule = (data) => api.post(`${LX}/notification-rules`, data).then(body);

/** Any rule field; pass `version` for optimistic concurrency. */
export const updateRule = (id, data) =>
  api.patch(`${LX}/notification-rules/${id}`, data).then(body);

/** Render the rule for an existing event and send it to me only (in-app). */
export const testRule = (id, eventId) =>
  api.post(`${LX}/notification-rules/${id}:test`, { event_id: eventId }).then(body);

// ── Recipient groups ─────────────────────────────────────────────────────────

export const listGroups = () => api.get(`${LX}/recipient-groups`).then(body);

/** { name, members: [{kind: 'user'|'role'|'contact'|'telegram_group', ref, channel?}] } */
export const createGroup = (data) => api.post(`${LX}/recipient-groups`, data).then(body);

export const updateGroup = (id, data) => api.patch(`${LX}/recipient-groups/${id}`, data).then(body);

// ── Templates ────────────────────────────────────────────────────────────────

/** Filters: key, channel, language. */
export const listTemplates = (params) =>
  api.get(`${LX}/message-templates`, { params: clean(params) }).then(body);

/**
 * Create/change this organisation's override of (key, channel, language):
 * { key, channel, language, body?, buttons? [{id, label}] (≤ 3), category?,
 *   provider_template_name?, active? }. A WhatsApp body change goes back to 'draft'.
 */
export const updateTemplate = (data) => api.patch(`${LX}/message-templates`, data).then(body);

/**
 * Render a template with sample vars (also the dispatch preview):
 * { key, channel, language, vars, body? (unsaved edit), buttons? }.
 */
export const previewTemplate = (data) =>
  api.post(`${LX}/message-templates:preview`, data).then(body);

// ── Budgets and tenant settings ──────────────────────────────────────────────

/** month: 'YYYY-MM' (default: this month, SAST). */
export const getBudgets = (month) =>
  api.get(`${LX}/channel-budgets`, { params: clean({ month }) }).then(body);

/** { month?, budgets: [{channel, budget_zar, soft_cap_zar, hard_cap_zar, at_cap}] } */
export const updateBudgets = (data) => api.patch(`${LX}/channel-budgets`, data).then(body);

export const getTenantSettings = () => api.get(`${LX}/tenant-settings`).then(body);

/**
 * { deviation_decision_sla_s?, ack_timeout_s?, auto_signoff_days?, review_threshold?,
 *   driver_default_language?, ack_reminders? (0 | 1 | 2, migration 034) }
 */
export const updateTenantSettings = (data) => api.patch(`${LX}/tenant-settings`, data).then(body);

// ── Delivery log, cost, SLO ──────────────────────────────────────────────────

/** Filters: channel, state (lists), direction, driver_id, trip_id, rule_id, event_id, intent, from, to, page, limit. */
export const listMessages = (params) =>
  api.get(`${LX}/messages`, { params: query(params) }).then(body);

export const getMessage = (id) => api.get(`${LX}/messages/${id}`).then(body);

/** { month?: 'YYYY-MM', channel?, page?, limit? } */
export const getCostLedger = (params) =>
  api.get(`${LX}/cost-ledger`, { params: clean(params) }).then(body);

/** { channel?, from?, to? } (default: the last 24 h) */
export const getDeliverySlo = (params) =>
  api.get(`${LX}/delivery-slo`, { params: clean(params) }).then(body);

// ── In-app notification centre ───────────────────────────────────────────────

/** { unread?: true, before?: ISO (paging), limit? } */
export const listNotifications = (params) =>
  api.get(`${LX}/notifications`, { params: clean(params) }).then(body);

/** ids: array of notification ids, or 'all'. */
export const markNotificationsRead = (ids) =>
  api.post(`${LX}/notifications:markRead`, ids === 'all' ? { all: true } : { ids }).then(body);

// ── Realtime ─────────────────────────────────────────────────────────────────

export const LEXISTAR_NOTIFICATION = 'lexistar:notification';

/**
 * Re-join `user:{own id}` on an already-connected /tracking socket (the server also joins it
 * at connect). Resolves to the server's ack ({ ok, room } | { ok: false, error }).
 * @param {import('socket.io-client').Socket} socket
 */
export const joinUserRoom = (socket) =>
  new Promise((resolve) => {
    socket.emit('lexistar:join-user', {}, (ack) => resolve(ack || { ok: false }));
  });
